import type { TgLinkStatusDto } from '@ledokol/contracts'

/**
 * Эпик «UI tg-link сотрудника» (T12) P2 — pure controller поллинга /me/tg/link/status.
 *
 * Дизайн: **pure controller + thin React wrapper** (см. `use-tg-link-polling.ts`).
 * Pure часть принимает Clock и Deps как dependency injection — тестируется без React/jsdom,
 * без реальных таймеров. React-обёртка только wire'ит к useState/useEffect/document.
 *
 * **5 защит от вечного фонового трафика** (план §1.2 + ревью P2):
 *   1. success — state перешёл в awaiting-confirm/linked → STOP насовсем
 *   2. TTL    — 15 мин (сервер pretty TTL кода) → STOP насовсем
 *   3. limit  — 300 попыток (страховка от багов: 15 мин × 60 / 3с = 300) → STOP
 *   4. pause/resume — visibilitychange (НЕ stop! заморозка интервала, продолжение с тем же кодом)
 *   5. unmount — React cleanup → STOP + isMounted-гард против setState-after-unmount
 *
 * **Разделение stop vs pause** (ревью P2 пункт 2):
 *   stop() — финал, очищает interval + ttl-timeout, вызывает onStop(reason). Cleanup всего.
 *   pause()/resume() — заморозка интервала. ttl-timeout НЕ сбрасывается (TTL абсолютный
 *   от старта; если вкладка скрыта дольше TTL — на resume первый же tick словит TTL).
 *
 * **TTL vs limit, что первым на ~900с** (ревью P2 пункт 3): TTL побеждает.
 *   Обоснование: TTL от сервера (источник истины, 15 мин по data-model.md §20), limit
 *   только страховка от багов. setTimeout(ttlMs) регистрируется первым; на одном и том же
 *   deadline JS event loop выполняет timer'ы в порядке регистрации (FIFO) → ttl сработает,
 *   stop() очистит всё, limit-tick не выполнится. Гарантия порядка проверяется в pure-тесте
 *   через FakeClock (FIFO для same-deadline).
 *
 * **isMounted-гард** (ревью P2 пункт 1): unmount во время in-flight fetchStatus → промис
 *   резолвится → isMounted() === false → onStatus НЕ вызывается → нет setState-after-unmount.
 */

export type StopReason = 'success' | 'ttl' | 'limit' | 'unmount'

/** Абстракция таймеров — для подмены FakeClock в тестах. */
export interface PollingClock {
  setInterval(cb: () => void, ms: number): number
  clearInterval(id: number): void
  setTimeout(cb: () => void, ms: number): number
  clearTimeout(id: number): void
}

export interface PollingDeps {
  /** Источник статуса — обычно `fetchTgLinkStatus()` из tg-link.ts (DI для тестов). */
  fetchStatus: () => Promise<TgLinkStatusDto>
  /** Колбэк на каждый успешный fetch (для React: setState). Не вызывается после stop. */
  onStatus: (status: TgLinkStatusDto) => void
  /** Колбэк на остановку — для React UI и логов. Вызывается ровно один раз. */
  onStop: (reason: StopReason) => void
  /** Гард: false → onStatus НЕ вызывается даже если fetch вернулся (защита unmount). */
  isMounted: () => boolean
  /** Подмена таймеров для тестов. По умолчанию — реальные window-таймеры. */
  clock?: PollingClock
}

export interface PollingHandle {
  /** visibilitychange hidden — пауза интервала. ttl-timeout продолжает идти. */
  pause(): void
  /** visibilitychange visible — immediate fetch + восстановление интервала. */
  resume(): void
  /** Внешний stop (например, из React unmount). Вызывает onStop('unmount'). */
  stop(): void
}

const INTERVAL_MS = 3_000
const TTL_MS = 15 * 60 * 1_000 // 900_000 — синхронно с data-model.md §20 (TgLinkToken.expiresAt)
const ATTEMPT_LIMIT = 300 // = TTL_MS / INTERVAL_MS (страховка)

const defaultClock: PollingClock = {
  setInterval: (cb, ms) => window.setInterval(cb, ms) as unknown as number,
  clearInterval: (id) => window.clearInterval(id),
  setTimeout: (cb, ms) => window.setTimeout(cb, ms) as unknown as number,
  clearTimeout: (id) => window.clearTimeout(id),
}

/**
 * Запустить поллинг. Возвращает handle с pause/resume/stop.
 * После stop() (или внутреннего STOP по success/ttl/limit) handle становится no-op.
 */
export function startTgLinkPolling(deps: PollingDeps): PollingHandle {
  const clock = deps.clock ?? defaultClock
  let intervalId: number | null = null
  let stopped = false
  let attempts = 0

  // ttl-timeout регистрируется ПЕРВЫМ — гарантирует FIFO-порядок при same-deadline (см. JSDoc)
  const ttlId = clock.setTimeout(() => doStop('ttl'), TTL_MS)

  function doStop(reason: StopReason): void {
    if (stopped) return // защита от двойного stop (например, ttl + unmount race)
    stopped = true
    if (intervalId !== null) clock.clearInterval(intervalId)
    intervalId = null
    clock.clearTimeout(ttlId)
    deps.onStop(reason)
  }

  function tick(): void {
    if (stopped) return // защита: tick может прилететь после clearInterval (микро-race)
    attempts++
    if (attempts > ATTEMPT_LIMIT) {
      doStop('limit')
      return
    }
    void deps.fetchStatus().then(
      (status) => {
        if (stopped) return // ушли в stop пока fetch шёл
        if (!deps.isMounted()) return // unmount-гард (даже если stop ещё не вызван)
        deps.onStatus(status)
        // success-stop: достигли C (awaiting-confirm) или D (linked)
        if (status.linked || status.pending !== null) doStop('success')
      },
      () => {
        // Сетевая ошибка не останавливает поллинг — может починиться на следующем тике.
        // Если сеть упадёт навсегда — лимит 300 / TTL 15 мин страхуют.
      },
    )
  }

  // Стартуем интервал сразу (без immediate fetch — React-обёртка делает initial fetch при mount)
  intervalId = clock.setInterval(tick, INTERVAL_MS)

  return {
    pause(): void {
      if (stopped) return
      if (intervalId !== null) {
        clock.clearInterval(intervalId)
        intervalId = null
      }
      // ttl-timeout НЕ трогаем — TTL абсолютный от старта (15 мин в reale time)
    },
    resume(): void {
      if (stopped) return
      if (intervalId !== null) return // уже идёт (двойной resume → no-op)
      // Immediate fetch при возврате на вкладку — иначе ждать до 3с зря
      tick()
      if (stopped) return // immediate tick мог уже остановить (success/limit)
      intervalId = clock.setInterval(tick, INTERVAL_MS)
    },
    stop(): void {
      doStop('unmount')
    },
  }
}
